import type { ISession } from "@mne-authentication/api";
import { configureStore } from "@reduxjs/toolkit";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";

import { snackbarMiddleware } from "@mnemonic/feature-snackbar";
import type { IntlShape } from "@mnemonic/intl";
import type { IEnvConfig } from "@mnemonic/pure-utils/ArgusUtils";

import baseApi from "./Service/baseApi";
import reducer from "./reducer";

export interface IExtraThunkArgs {
  session: ISession | null;
  target: IEnvConfig;
  intl: IntlShape;
}

export type TAppState = ReturnType<typeof reducer>;
export type TAppDispatch = ReturnType<typeof createStore>["dispatch"];
export const useTypedSelector: TypedUseSelectorHook<TAppState> = useSelector;
export const useTypedDispatch: () => TAppDispatch = useDispatch;

interface ICreateStoreArgs {
  extraArgument: IExtraThunkArgs;
  preloadedState?: Partial<TAppState>;
}

export default function createStore({ extraArgument, preloadedState = {} }: ICreateStoreArgs) {
  return configureStore({
    reducer,
    devTools: process.env.NODE_ENV !== "production",
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument,
        },
        serializableCheck: false,
        immutableCheck: false,
      }).concat(baseApi.middleware, snackbarMiddleware?.middleware),
    preloadedState,
  });
}
